import { createEvent, restore, sample } from 'effector'
import { fx } from '../../../../libs'
import {
  CompleteRevisionRequest,
  CreateRevisionRequest,
  RevisionItemDetailFromServer,
  RevisionsHistoryRequest,
  RevisionsHistoryResponse,
  UpdateRevisionRequest,
  CorrectionRequest,
  RecalculateRequest,
} from './types'
import { getWallet } from '../../wallets/details/model'
import { $params } from '../../../../features/router'
import {
  showNotification,
  NotificationPayload,
} from '../../../../features/notifications'

export const revisionMounted = createEvent()

export const revisionsHistoryMounted = createEvent<RevisionsHistoryRequest>()

export const updateRevisionMounted = createEvent<UpdateRevisionRequest>()

export const completeRevisionMounted = createEvent<CompleteRevisionRequest>()

export const createRevisionMounted = createEvent<CreateRevisionRequest>()

export const walletMounted = createEvent()

export const sendCorrectionRequest = createEvent<CorrectionRequest>()

export const recalculateBalanceEvent = createEvent<RecalculateRequest>()

export const recalculateBalance = fx<RecalculateRequest, void>({
  method: 'POST',
  url: `/finance/wallet/recalculate`,
})

export const getRevisionsHistory = fx<void, RevisionsHistoryResponse>({
  method: 'GET',
  url: '/finance/wallet/{id}/revision',
})

export const $revisionsHistory = restore<RevisionItemDetailFromServer[]>(
  getRevisionsHistory.doneData.map((a) => a.body.items),
  [],
)

export const $totalCountOfRevisionsHistory = restore(
  getRevisionsHistory.doneData.map((a) => a.body.total),
  0,
)

export const updateRevision = fx<
  UpdateRevisionRequest,
  RevisionItemDetailFromServer
>({
  method: 'PUT',
  url: '/finance/revision/{id}',
})

export const completeRevision = fx<
  CompleteRevisionRequest,
  { id: number; result: boolean }
>({
  method: 'PUT',
  url: '/finance/revision/{id}/complete',
})

export const createRevision = fx<
  CreateRevisionRequest,
  RevisionItemDetailFromServer
>({
  method: 'POST',
  url: '/finance/revision',
})

export const sendCorrection = fx<
  CorrectionRequest,
  { id: number; discrepancy: number; comment: string }
>({
  method: 'POST',
  url: '/finance/correction',
})

export const $updatedRevisionData = restore<RevisionItemDetailFromServer>(
  updateRevision.doneData.map((a) => a.body),
  {} as RevisionItemDetailFromServer,
)

export const $completedRevisionData = restore<{ id: number; result: boolean }>(
  completeRevision.doneData.map((a) => a.body),
  { id: 0, result: false },
)

export const $correctionSaveData = restore<{
  id: number
  discrepancy: number
  comment: string
}>(
  sendCorrection.doneData.map((a) => a.body),
  { id: 0, discrepancy: 0, comment: '' },
)

export const $createdRevisionData = restore<RevisionItemDetailFromServer>(
  createRevision.doneData.map((a) => a.body),
  {} as RevisionItemDetailFromServer,
)

sample({
  // @ts-ignore
  clock: revisionsHistoryMounted,
  fn: ({ limit, page, currency, walletId }) => {
    let filtersTransactionsParams

    if (currency) {
      filtersTransactionsParams = {
        currency,
      }
    }

    let query = {
      limit,
      page,
    }

    // @ts-ignore
    query['order'] = '-createdAt'

    // @ts-ignore
    query['where'] = `${JSON.stringify(filtersTransactionsParams)}`

    return {
      url: `/finance/wallet/${walletId}/revision`,
      query,
    }
  },
  target: getRevisionsHistory,
})

sample({
  // @ts-ignore
  clock: updateRevisionMounted,
  fn: ({ id, psBalance }) => {
    return {
      body: {
        psBalance,
      },
      url: `/finance/revision/${id}`,
    }
  },
  target: updateRevision,
})

sample({
  // @ts-ignore
  clock: completeRevisionMounted,
  fn: ({ id }) => {
    return {
      url: `/finance/revision/${id}/complete`,
    }
  },
  target: completeRevision,
})

sample({
  // @ts-ignore
  clock: createRevisionMounted,
  fn: ({ wallet, psBalance, currency, crmBalance }) => {
    return {
      body: {
        wallet,
        psBalance,
        currency,
        crmBalance,
      },
      url: `/finance/revision`,
    }
  },
  target: createRevision,
})

sample({
  clock: walletMounted,
  source: $params,
  fn: (params) => {
    return {
      url: `/finance/wallet/${params?.walletID}`,
    }
  },
  target: getWallet,
})

sample({
  clock: sendCorrectionRequest,
  fn: ({ revision, comment, discrepancy, discrepancyUsd }) => ({
    body: {
      revision,
      comment,
      discrepancy,
      discrepancyUsd,
    },
    url: '/finance/correction',
  }),
  target: sendCorrection,
})

sample({
  clock: recalculateBalanceEvent,
  fn: (recalcData) => {
    return {
      body: {
        ...recalcData,
      },
    }
  },
  target: recalculateBalance,
})

sample({
  clock: recalculateBalance.doneData,
  fn: (): NotificationPayload => ({
    type: 'success',
    message: 'Success',
    description: 'Balance recalculated successfully',
    placement: 'topRight',
  }),
  target: [showNotification, walletMounted],
})

sample({
  clock: recalculateBalance.failData,
  fn: (): NotificationPayload => ({
    type: 'error',
    message: 'Error',
    description: 'Failed to recalculate balance',
    placement: 'topRight',
  }),
  target: showNotification,
})
